/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Component } from '@/components/region/component';
import { ComponentDataProvider, useComponentData, type ComponentType } from '@/components/region';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import { NavLink } from '@/components/link';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { classifyComponents } from './nav-menu-page';

// ---------------------------------------------------------------------------
// Mobile-specific styles
// ---------------------------------------------------------------------------

const MOBILE_MENU_CSS = `
/* Stack all subcategory columns vertically */
.fj-mobile-menu .subcategories__cols__wrapper {
    display: flex !important;
    flex-direction: column;
    width: 100%;
    gap: 0;
}
.fj-mobile-menu .subcategories__col {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
}

/* Left-nav .subcategories blocks — also stacked, no gap */
.fj-mobile-menu .subcategories {
    width: 100%;
}
.fj-mobile-menu .subcategories + .subcategories {
    margin-top: 0 !important;
}

/* Section heading row: flex so toggle sits flush right */
.fj-mobile-menu .subcategories__name,
.fj-mobile-menu .subcategories__name--no-link {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    padding: 19px 35px 19px 25px;
    margin: 0;
    font-family: var(--font-sans);
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.05em;
    cursor: pointer;
    color: inherit;
    /* Border removed from heading — it lives on the section container below
       so it always appears after the last visible item (L3 when expanded). */
}
.fj-mobile-menu .subcategories__name a {
    padding: 0;
}
.fj-mobile-menu .subcategory-banner {
}
/* Border sits at the bottom of each accordion section container so it trails
   the L3 list when the section is expanded, and the heading when collapsed. */
.fj-mobile-menu .subcategories:has(> .subcategories__name),
.fj-mobile-menu .subcategories:has(> .subcategories__name--no-link),
.fj-mobile-menu .subcategories__col:has(> .subcategories__name),
.fj-mobile-menu .subcategories__col:has(> .subcategories__name--no-link) {
    border-bottom: 1px solid #00000026;
}
.fj-mobile-menu .subcategories__name {
    margin-top: 0 !important;
}
.fj-mobile-menu .animate-charcter {
    font-size: 24px;
}
.fj-mobile-menu .subcategories__name__link {
    flex: 1;
    font-size: 22px;
    font-weight: 700;
    text-decoration: none;
    color: inherit;
}

/* Toggle button: hidden by default; JS adds .fj-expandable to sections that
   have L3 children, which reveals the button via the rule below. */
.fj-mobile-menu .subcategories__name__toggle-btn {
    display: none !important;
}
.fj-mobile-menu .fj-expandable > .subcategories__name .subcategories__name__toggle-btn,
.fj-mobile-menu .fj-expandable > .subcategories__name--no-link .subcategories__name__toggle-btn {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 17px;
    height: 34px;
    background: none;
    border: none;
    font-size: 30px;
    font-weight: 500;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    color: inherit;
}

/* Hide the PD inline "View All" link — the sole View All is in the header. */
.fj-mobile-menu .level-2__category__view-all__link {
    display: none !important;
}

/* Show mobile-only PD elements (hidden on desktop) */
.fj-mobile-menu .display-mobile-only,
.fj-mobile-menu .subcategories__list__item__link--mobile-only {
    display: block !important;
}
.fj-mobile-menu .subcategories__list__item:has(.display-mobile-only),
.fj-mobile-menu .subcategories__list__item:has(.subcategories__list__item__link--mobile-only) {
    display: list-item !important;
}

/* Link list */
.fj-mobile-menu .subcategories__list {
    list-style: none;
    /* margin-top: 15px; */
    padding: 0 0 20px;
}
.fj-mobile-menu .subcategories__list__item {
    margin: 0;
}
.fj-mobile-menu .subcategories__list__item__link {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: var(--font-sans);
    padding: 0 25px;
    font-size: 16px;
    font-weight: 400;
    line-height: 30px;
    color: #1c1e29;
    text-decoration: none;
    column-gap: unset;
    flex-wrap: unset;
}
.fj-mobile-menu .subcategories__list__item__link img, 
.fj-mobile-menu .subcategories__list__item__link svg {
    margin-right: 16px;
}
.fj-mobile-menu .subcategories__list__item__link:hover {
    text-decoration: underline;
}

/* Sub-headings within a section (e.g. "By Width", "Best Sellers") */
.fj-mobile-menu .subcategories__sub-name {
    font-weight: 700;
    font-size: 16px;
    line-height: 30px;
    letter-spacing: normal;
    padding: 4px 25px;
    margin-top: 35px
    margin: 0;
    text-transform: none;
}

/* Badge ("New") — keep inline beside the link text */
.fj-mobile-menu .badge-outlined {
    margin-left: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: fit-content;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 2px 6px;
    border-radius: 3px;
    border: 1.5px solid #1a1a1a;
    color: #1a1a1a;
    line-height: 1;
    vertical-align: middle;
    transition: all 0.25s ease
}

/* Insider card */
.fj-mobile-menu .fj-insider-card,
.fj-mobile-menu .header__insider {
    margin-top: 20px;
    margin-bottom: 40px;
    padding-top: 40px;
    padding-left: 25px;
    padding-right: 25px;
}

.fj-mobile-menu .header__insider__title {
    margin: 0 0 16px;
    text-transform: none;
    font-size: 24px;
    font-weight: 700;
    line-height: 29px;
    color: #1c1e29;
}

.fj-mobile-menu .header__insider__description {
    margin: 0 0 16px;
    text-transform: none;
    font-size: 16px;
    font-weight: 400;
    line-height: 19px;
    color: #1c1e29;
}

.fj-mobile-menu .header__insider__buttons {
    display: flex;
    gap: 10px;
}

.fj-mobile-menu .header__insider__buttons__button {
    padding: 10px 16px;
    height: auto;
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: .0125em;
    color: #1c1e29;
    background: #fff;
    border: 1px solid;
    border-radius: 3px;
    text-decoration: none;
}

.fj-mobile-menu .header__insider__buttons__button--sign-up {
    color: #fff;
    background: #1c1e29;
    border-color: #1c1e29;
}

.fj-mobile-menu  .header__insider .cta-button {
    display: inline-block;
    border: 1px solid #000;
    padding: 14px;
    max-width: 92px;
    padding-top: 5px;
    font-size: 19px;
    font-weight: 700;
    padding-bottom: 5px;
    line-height: 24px;
}

/* Collapsed state: hide everything except the heading */
.fj-mobile-menu .fj-section-collapsed > *:not(.subcategories__name):not(.subcategories__name--no-link) {
    display: none !important;
}

/* Inside the accordion, banner components are rendered as plain link rows.
   We hide only the image so the product-name link text stays visible. */
.fj-mobile-menu .fj-mega-banner,
.fj-mobile-menu .subcategory__promotion {
    display: none !important;
}
/* Reset desktop absolute-positioned banner to a plain block */
.fj-mobile-menu .subcategory-banner__wrapper {
    display: block !important;
    position: static !important;
    width: 100% !important;
    height: auto !important;
    overflow: visible !important;
    text-decoration: none;
    color: inherit;
}
/* Hide banner image inside the accordion */
.fj-mobile-menu .subcategory-banner__image,
.fj-mobile-menu .subcategory-banner__image__img {
    display: none !important;
}
/* Show details block as a plain row */
.fj-mobile-menu .subcategory-banner__details {
    display: none !important;
    position: static !important;
    padding: 25px !important;
    color: #000000 !important;
    background: transparent !important;
}
.fj-mobile-banner .subcategory-banner__details {
    position: static !important;
    padding: 25px !important;
    color: #000000 !important;
    background: transparent !important;
}
.fj-mobile-banner .subcategory-banner__product-name__link {
    display: block !important;
    font-size: 24px !important;
    font-weight: 700 !important;
    margin-bottom: 10px !important;
    color: #000000 !important;
    text-decoration: none !important;
    letter-spacing: normal !important;
    text-transform: none !important;
}
.fj-mobile-banner .subcategory-banner__product-name__link:hover {
    text-decoration: underline !important;
}
.fj-mobile-menu .subcategory-banner__description,
.fj-mobile-menu .subcategory-banner__cta {
    display: none !important;
}
.fj-mobile-banner .subcategory-banner__description {
    font-size: 16px !important;
    line-height: 19px !important;
    text-transform: none !important;
    margin: 10px 0 30px !important;
    white-space: normal !important;
}

.fj-mobile-banner .subcategory-banner__cta {
    display: inline-block !important;
    margin: 0 !important;
    padding: 10px 16px !important;
    width: auto !important;
    border: 1px solid !important;
    border-radius: 2px !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    line-height: 17px !important;
    text-transform: uppercase !important;
}

/* ── Banner carousel items ──────────────────────────────────────────────── */

/* Reset the desktop fixed-dimension banner styles */
.fj-mobile-banner .subcategory-banner,
.fj-mobile-banner .subcategory-banner__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100% !important;
    height: auto !important;
    overflow: hidden;
    text-decoration: none;
}
/* Portrait image that fills the tile width */
.fj-mobile-banner .subcategory-banner__image__img,
.fj-mobile-banner img {
    display: block;
    width: 100% !important;
    max-width: 100% !important;
    height: 460px;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    border: 0;
}
/* Caption sits below the image (not overlaid) */
.fj-mobile-banner .subcategory-banner__details {
    position: static !important;
    padding: 10px 8px;
    background: #fff;
    color: #1c1e29;
}

/* Carousel arrow — white square button, same chevron pattern as desktop */
.fj-mobile-banner-arrow {
    position: absolute !important;
    top: 40% !important;
    transform: translateY(-50%) !important;
    width: 45px !important;
    height: 60px !important;
    background: var(--white, #fff) !important;
    border-radius: 3px !important;
    border: none !important;
    opacity: 1 !important;
    z-index: 2;
    cursor: pointer;
    border-radius: 3px;
    box-shadow: 0 4px 4px rgba(0, 0, 0, .25);
}
.fj-mobile-banner-arrow svg { display: none !important; }
.fj-mobile-banner-arrow::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 18px;
    height: 18px;
    border-top: 4px solid #1c1e29;
    border-left: 4px solid #1c1e29;
}
.fj-mobile-banner-arrow--next {
    right: 18px !important;
    left: auto !important;
}
.fj-mobile-banner-arrow--next::after {
    transform: translate(-65%, -50%) rotate(135deg);
}
.fj-mobile-banner-arrow--prev {
    left: 0 !important;
    right: auto !important;
}
.fj-mobile-banner-arrow--prev::after {
    transform: translate(-35%, -50%) rotate(-45deg);
}
.fj-mobile-banner-arrow:disabled { display: none !important; }

/* ── Promo / shipping bar ───────────────────────────────────────────────── */
.fj-mobile-promo .subcategory__promotion {
    margin: 0;
    display: flex;
    flex-direction: column;
    font-family: var(--font-sans);
    text-align: center;
    font-weight: 700;
    font-size: 16px;
    line-height: 36px;
    color: #1c1e29;
}
.fj-mobile-promo .subcategory__promotion__link {
    color: #1c1e29;
    font-size: 16px;
    font-weight: 400;
    line-height: normal;
    text-transform: none;
    text-decoration: underline
}
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface NavMenuPageMobileProps {
    page: PageWithComponentData;
    onClose?: () => void;
    /** Called once after mount with the href found in the PD "View All" link,
     *  so the parent header can use the PD-authored URL instead of a constructed one. */
    onViewAllUrl?: (url: string) => void;
}

export function NavMenuPageMobile({ page, onClose, onViewAllUrl }: NavMenuPageMobileProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const cleanups: (() => void)[] = [];
        let firstExpandable = true;

        const setupSection = (section: HTMLElement) => {
            // Skip already-processed sections.
            if (section.hasAttribute('data-fj-processed')) return;
            section.setAttribute('data-fj-processed', '1');

            const heading = section.querySelector<HTMLElement>(
                ':scope > .subcategories__name, :scope > .subcategories__name--no-link'
            );
            if (!heading) return;

            const toggleBtn = heading.querySelector<HTMLElement>('.subcategories__name__toggle-btn');

            // Discover/Explore sections are always expanded — skip all accordion logic.
            if (section.closest('.fj-discover-sections')) {
                if (onClose) {
                    heading.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
                        link.addEventListener('click', onClose);
                        cleanups.push(() => link.removeEventListener('click', onClose));
                    });
                }
                return;
            }

            // Only treat this section as expandable if it actually has L3 list items.
            const hasChildren = section.querySelectorAll('.subcategories__list li').length > 0;

            if (!hasChildren) {
                // Leaf section — no toggle. Heading link navigates normally and closes the menu.
                if (onClose) {
                    heading.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
                        link.addEventListener('click', onClose);
                        cleanups.push(() => link.removeEventListener('click', onClose));
                    });
                }
                return;
            }

            // Expandable section — add CSS marker so the toggle button becomes visible.
            section.classList.add('fj-expandable');

            if (firstExpandable) {
                firstExpandable = false;
                if (toggleBtn) toggleBtn.textContent = '−';
            } else {
                section.classList.add('fj-section-collapsed');
                if (toggleBtn) toggleBtn.textContent = '+';
            }

            // Prevent the heading's <a> from navigating — clicking only expands/collapses.
            const preventNav = (e: Event) => e.preventDefault();
            heading.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
                link.addEventListener('click', preventNav);
                cleanups.push(() => link.removeEventListener('click', preventNav));
            });

            const handleToggle = (e: Event) => {
                e.preventDefault();
                const collapsed = section.classList.contains('fj-section-collapsed');
                section.classList.toggle('fj-section-collapsed');
                if (toggleBtn) toggleBtn.textContent = collapsed ? '−' : '+';
            };

            // Only attach to the heading — toggleBtn is inside the heading so its
            // clicks already bubble up here. Adding a separate listener on toggleBtn
            // causes handleToggle to fire twice, toggling the section back to its
            // original state and making +/- appear broken.
            heading.addEventListener('click', handleToggle);
            cleanups.push(() => heading.removeEventListener('click', handleToggle));
        };

        // Collect accordion sections in DOM order.
        // Left-nav components render .subcategories blocks (each with a direct heading).
        // Middle component renders .subcategories__col blocks inside .subcategories__cols__wrapper.
        // Filter to elements that own a DIRECT heading child so nested structures are
        // never double-targeted.
        const collectSections = () =>
            Array.from(
                container.querySelectorAll<HTMLElement>('.subcategories, .subcategories__col')
            ).filter((el) =>
                el.querySelector(':scope > .subcategories__name, :scope > .subcategories__name--no-link')
            );

        collectSections().forEach(setupSection);

        // PD components resolve their data asynchronously, so new sections can appear
        // in the DOM after the initial pass. Watch for them and collapse immediately —
        // the first expandable section has already been claimed above.
        const observer = new MutationObserver(() => {
            collectSections().forEach(setupSection);
        });
        observer.observe(container, { childList: true, subtree: true });
        cleanups.push(() => observer.disconnect());

        // Attach onClose to every navigable link EXCEPT heading links of expandable
        // sections (those have preventNav + handleToggle instead).
        // This covers list-item links, leaf-section heading links, and banner
        // product-name links rendered in the accordion for non-Men/Women categories.
        if (onClose) {
            container.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
                const inExpandableHeading =
                    link.closest('.fj-expandable > .subcategories__name') ||
                    link.closest('.fj-expandable > .subcategories__name--no-link');
                if (!inExpandableHeading) {
                    link.addEventListener('click', onClose);
                    cleanups.push(() => link.removeEventListener('click', onClose));
                }
            });
        }

        // Extract the View All URL from the PD markup and report it to the parent header
        // so it uses the PD-authored href exactly as written, without any transformation.
        if (onViewAllUrl) {
            const pdViewAll = container.querySelector<HTMLAnchorElement>('.level-2__category__view-all__link');
            const rawHref = pdViewAll?.getAttribute('href');
            if (rawHref) {
                onViewAllUrl(rawHref);
            }
        }

        return () => cleanups.forEach((fn) => fn());
    }, [page, onClose, onViewAllUrl]);

    const region = page.regions?.[0];
    const components = (region?.components ?? []) as ComponentType[];
    if (!region || !components.length) return null;

    const regionId = region.id;
    const { left, middle, banners, promo } = classifyComponents(components);
    // Men/Women have a middle column (6-column subcategory block) + a single desktop
    // promo banner. All other categories (Gloves, Gear, Fan Shop…) have no middle
    // column — their banners ARE the sub-category image tiles and must be shown.
    const hasMiddle = middle.length > 0;

    const content = (
        <>
            {/* eslint-disable-next-line react/no-danger */}
            <style dangerouslySetInnerHTML={{ __html: MOBILE_MENU_CSS }} />

            {/* Accordion sections.
                "Discover"/"Explore" is always the last left-nav component (index 1+).
                We render it AFTER the main content so it sits just above the promo bar.
                It is wrapped in .fj-discover-sections so the useEffect can identify it
                and keep it always expanded with no toggle. */}
            <div ref={containerRef} className="fj-mobile-menu">
                {left.slice(0, 1).map((c) => (
                    <Component key={c.id} component={c} regionId={regionId} />
                ))}
                {hasMiddle
                    ? middle.map((c) => <Component key={c.id} component={c} regionId={regionId} />)
                    : [...banners, ...middle].map((c) => <Component key={c.id} component={c} regionId={regionId} />)}
                {left.length > 1 && (
                    <div className="fj-discover-sections">
                        {left.slice(1).map((c) => (
                            <Component key={c.id} component={c} regionId={regionId} />
                        ))}
                    </div>
                )}
            </div>

            {/* Banner peek carousel — shown for all categories that have banners AND
                no middle column (i.e. not Men/Women whose single banner is desktop-only).
                Non-Men/Women categories may pack all tiles into one banner component so
                banners.length > 1 is not a reliable discriminator — hasMiddle is. */}
            {!hasMiddle && banners.length > 0 && (
                <div className="py-10 overflow-hidden relative">
                    <Carousel opts={{ align: 'center', dragFree: true }}>
                        <CarouselContent className="-ml-3 pl-4 pr-4">
                            {banners.map((c) => (
                                <CarouselItem key={c.id} className="pl-0 pr-4 first:ml-[13%] last:mr-[13%] basis-[75%]">
                                    <div className="fj-mobile-banner">
                                        <Component component={c} regionId={regionId} />
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <CarouselPrevious className="fj-mobile-banner-arrow fj-mobile-banner-arrow--prev" />
                        <CarouselNext className="fj-mobile-banner-arrow fj-mobile-banner-arrow--next" />
                    </Carousel>
                </div>
            )}

            {/* Promo / shipping bar — always shown at the bottom */}
            <div className="border-t border-[#00000026] pt-[13px] pb-[35px] text-center">
                {promo ? (
                    <div className="fj-mobile-promo">
                        <Component component={promo} regionId={regionId} />
                    </div>
                ) : (
                    <p className="flex justify-center flex-col text-base font-bold text-center m-0 leading-[36px] text-[#1c1e29]">
                        Free Shipping on all orders over $150+{' '}
                        <NavLink to="/shipping" className="font-normal text-base font-normal text-center m-0 text-[#1c1e29] leading-[18px] underline">
                            LEARN MORE
                        </NavLink>
                    </p>
                )}
            </div>
        </>
    );

    return <MobileNavDataProvider componentData={page.componentData}>{content}</MobileNavDataProvider>;
}

function MobileNavDataProvider({
    componentData,
    children,
}: {
    componentData?: Record<string, Promise<unknown>>;
    children: ReactNode;
}) {
    const existing = useComponentData();
    if (componentData && !existing) {
        return <ComponentDataProvider value={componentData}>{children}</ComponentDataProvider>;
    }
    return <>{children}</>;
}
